import { useState, useEffect } from "react";
import Image from "@/atoms/Image";
import H2 from "@/atoms/text/H2";
import Label from "@/atoms/text/Label";
import Para from "@/atoms/text/Para";
import RegistrationForm from "@/molecules/RegistrationForm";
import ListOverBgImg from "@/components/molecules/ListOverBgImg";
import VideoModalTrigger from "@/components/molecules/VideoModalTrigger";
import Video from "@/components/atoms/Video";
import { isMobileScreen } from "@/utils/functions";
import SubIntegrationTile from "@/components/molecules/SubIntegrationTile";
import PropTypes from "prop-types";

export default function FeatureSectionTwocol({
  bgColor,
  label,
  title,
  content,
  contentAdjToTitle,
  image,
  isImageOnLeft = true,
  stats,
  hasPlayBtn = false,
  ctaData = false,
  hasRegisterForm = false,
  formTitle,
  hasBusinessName = true,
  formBgColor = "white",
  videoSrc,
  listOverBgImgData,
  subIntegrationTileData = false,
  isTitleMd = false,
  hasVideoThumbnail = false,
}) {
  const { hasCta, text, href, isExternalLink = false } = ctaData;
  const [isMobile, setIsMobile] = useState(null);

  useEffect(() => {
    setIsMobile(isMobileScreen);
  }, []);

  return (
    <section className={`bc-${bgColor} pv-15-d pv-8 ps-r`}>
      <div className="container">
        <div
          className={`fx fx--c fx--r-d fx--sb ${
            subIntegrationTileData ? "" : "fx--cv"
          }`}
        >
          <article
            className={`content-wrapper ${
              isImageOnLeft ? "fx--order-2-d" : ""
            } w-50-d mb-reset-d ${contentAdjToTitle ? "mb-5" : " mb-7"}`}
          >
            {label && (
              <Label size="lg" color="blue-light" className="bold mb-3">
                {label}
              </Label>
            )}
            {title && (
              <H2
                desktopSize={`${isTitleMd ? "md" : "lg"}`}
                className={`bold ${contentAdjToTitle ? "" : "mb-5"}`}
                dangerouslySetInnerHTML={{ __html: title }}
              ></H2>
            )}
            {content?.map((text, idx) => {
              const contentKey = `content_${idx}`;
              return (
                <Para
                  key={contentKey}
                  className={idx === content.length - 1 ? "" : "mb-5"}
                  desktopSize="lg"
                  mobileSize="lg"
                  dangerouslySetInnerHTML={{ __html: text }}
                ></Para>
              );
            })}
            {hasCta && (
              <a
                href={href}
                className="btn btn--link btn--link-lg text--md mt-7"
                target={isExternalLink ? "_blank" : ""}
                rel={isExternalLink ? "noopener noreferrer" : ""}
              >
                {text}
              </a>
            )}
            {hasRegisterForm && (
              <RegistrationForm
                title={formTitle}
                bgColor={formBgColor}
                hasBusinessNameField={hasBusinessName}
              ></RegistrationForm>
            )}
            <style jsx>{`
              .content-wrapper {
                max-width: 540px;
              }
            `}</style>
          </article>
          {hasPlayBtn && (
            <div className="ps-r video-wrapper">
              {hasVideoThumbnail ? (
                <Image
                  fileName={hasVideoThumbnail.fileName}
                  desktopWidth={hasVideoThumbnail.desktopWidth}
                  mobileWidth={hasVideoThumbnail.mobileWidth}
                  alt={hasVideoThumbnail.altText}
                  fileFormat="png"
                />
              ) : (
                <Video
                  src="https://assets.answerforce.com/answerforce/us/website/media/ppnb-clip-with-logo.mp4"
                  posterSrcDesk="https://assets.answerforce.com/answerforce/us/website/images/desktop/ppnb-poster-with-logo-2x.jpg"
                  posterSrcMob="https://assets.answerforce.com/answerforce/us/website/images/mobile/ppnb-poster-with-logo-2x.jpg"
                />
              )}
              <VideoModalTrigger
                src={videoSrc}
                isBtnCenter
                btnSizeDesk="84"
                btnSizeMob="64"
              />
              <style jsx>{`
                .video-wrapper {
                  display: inline-block;
                  max-width: 500px;
                  ${hasVideoThumbnail ? "height: auto;" : "height: 360px;"}
                }
                @media (max-width: 650px) {
                  .video-wrapper {
                    ${hasVideoThumbnail ? "height: auto;" : "height: 242px;"}
                  }
                }
              `}</style>
            </div>
          )}
          {image && (
            <Image
              fileName={image.fileName}
              fileFormat="png"
              desktopWidth={image.desktopWidth}
              mobileWidth={image.mobileWidth}
              mobileHeight={image.mobileHeight}
              alt={image.altText}
              className={`${isImageOnLeft ? "fx--order-1-d" : ""} ${
                stats ? "mb-3 mb-reset-d" : ""
              }`}
            />
          )}
          {contentAdjToTitle && (
            <div className="w-50-d pl-7-d">
              {contentAdjToTitle.map((text, idx) => {
                const contentAdjToTitleKey = `contentAdjToTitle_${idx}`;
                return (
                  <Para
                    key={contentAdjToTitleKey}
                    className={`${
                      idx === contentAdjToTitle.length - 1 ? "" : "mb-5"
                    }`}
                    desktopSize="lg"
                    mobileSize="lg"
                    dangerouslySetInnerHTML={{ __html: text }}
                  ></Para>
                );
              })}
            </div>
          )}
        </div>
        {listOverBgImgData && <ListOverBgImg data={listOverBgImgData} />}
        {stats && (
          <Para
            className="stats"
            color="grey"
            desktopSize="xs"
            mobileSize="xs"
            dangerouslySetInnerHTML={{ __html: stats }}
          ></Para>
        )}
        {subIntegrationTileData && (
          <div className="ps-r fx fx--c fx--cv fx--r-d fx--sb-d mt-10">
            <Image
              isBannerImg={false}
              has2XWebp
              fileName="curve-arrow-2x"
              fileFormat="png"
              desktopWidth={`${isMobile ? 70 : 100}`}
              alt="Curved arrow"
              className="card-pointer"
            />
            {subIntegrationTileData.map((tile, idx) => {
              const subIntegrationTileKey = `subIntegrationTile_${idx}`;
              return (
                <SubIntegrationTile
                  key={subIntegrationTileKey}
                  bgColor={tile.bgColor}
                  logo={tile.logo}
                  title={tile.title}
                  content={tile.content}
                  badge={tile.badge}
                  className={isMobile && idx === 0 ? "mb-20 mb-rest-d" : ""}
                />
              );
            })}
          </div>
        )}
      </div>
    </section>
  );
}

FeatureSectionTwocol.propTypes = {
  subIntegrationTileData: PropTypes.array,
  content: PropTypes.arrayOf(PropTypes.string),
};
